/**
 * @fileoverview UI highlighting and real-time collaboration utilities for order management.
 * Manages visual indicators for users currently editing orders and coordinates
 * real-time collaboration features through WebSocket connections.
 *
 * @author Web Analysis Team
 * @version 1.0.0
 */

import SocketIO from './socket_io';
import { dayjs } from '../../../components/ex_dayjs';

/**
 * Initializes the Socket.IO connection and sets up UI highlighting for collaborative editing.
 * Manages color assignments for different users and coordinates real-time order editing indicators.
 *
 * @param {Object} app - Application configuration object
 * @param {Object} app.options - Connection and environment options
 * @param {Object} app.options.env - Environment configuration
 * @param {string} app.options.env.Identity - Current user identity/email
 * @param {string} app.options.env.FirstName - Current user's first name
 * @param {string} app.options.ioUrl - Socket.IO server URL
 * @param {Object} app.vendor - Vendor-specific configuration and methods
 * @param {Object} app.bus - Event bus for inter-component communication
 * @param {Object} app.bus_events - Event type constants
 * @param {Function} app.setEditingOrders - State setter for currently editing orders
 */
const initSocketIO = (app) => {
    const { options, vendor, bus, bus_events, setEditingOrders } = app;
    // console.log('_app', app);

    // onRefreshUI = function (order) {
    //     console.log("*** onRefreshUI ***", order);
    // };

    /**
     * Manages the display of orders currently being edited by different users.
     * Assigns unique colors to each user and updates the UI highlighting accordingly.
     *
     * @returns {Function} Handler function for processing opening orders events
     */
    const onOpeningOrders = (function () {
        let colourAssigns = {};
        
        /**
         * Gets or assigns a unique color ID for a user
         *
         * @param {string} by - User identifier (email)
         * @returns {number} Color ID (1-18) for the user
         */
        function getOrAssign (by) {
            if (colourAssigns.hasOwnProperty(by)) {
                return colourAssigns[by];
            } else {
                colourAssigns[by] = (Object.keys(colourAssigns).length % 18) + 1;
            }
            return colourAssigns[by];
        }

        /**
         * Processes the list of orders currently being edited and updates UI highlighting
         *
         * @param {Object} orders - Object mapping user IDs to order information
         * @param {string} orders[].orderNumber - Order number being edited
         * @param {string} orders[].nickName - Display name of the user editing
         */
        return function (orders) {
            Object.keys(orders).forEach(by => {
                let o = orders[by];
                let boxData = { By: by === options.env.Identity ? 'You' : o.nickName, DateTime: dayjs().fromNow(), Color: getOrAssign(by) };
                // if (by === options.env.Identity)
                //     console.log(`*** onOpeningOrders *** boxData - self`, boxData);
                o.email = by;
                o.boxData = boxData;
            });
            setEditingOrders(Object.entries(orders).map(x => x[1]));
        };
    })();

    // Create and configure the Socket.IO connection
    let socketIO = new SocketIO({
        identity: options.env.Identity,
        nickName: options.env.FirstName
    });
    
    // Establish connection and register event handlers
    socketIO.connect(vendor.getSocketIOEndPoint(options.ioUrl)).then(function () {
        socketIO.on(bus_events.opening_orders, onOpeningOrders);
        socketIO.on(bus_events.order_status_changed, bus.emit.bind(bus, bus_events.order_status_changed));
        socketIO.on(bus_events.broadcast, bus.emit.bind(bus));
        bus.ioUrl = socketIO.url;
    }).catch(console.error);

    /**
     * Handles grid selection changes by notifying other users of order opens
     *
     * @param {Object} order - Selected order object
     * @param {string|number} order.order_number - Order number being opened
     * @param {string} status - Current status of the order
     */
    bus.on(bus_events.grid_selection_changed, function onAnOrderOpened (order, status) {
        socketIO.open(order.order_number, status);
    });
    
    /**
     * Handles grid data refresh preparation by revoking current editing sessions
     */
    bus.on(bus_events.grid_before_send, function () {
        return socketIO.emit('revoke');
    });

    /**
     * Handles grid data load completion by refreshing the Socket.IO connection
     */
    bus.on(bus_events.grid_completed, function () {
        return socketIO.refresh();
    });
    
    // bus.on(bus_events.refresh_highlight_box, onRefreshUI);
    
    /**
     * Handles order status change requests by broadcasting through Socket.IO
     *
     * @param {Object} request - Status change request object
     * @returns {Promise<any>} Promise resolving to server response
     */
    bus.on(bus_events.order_status_changing, function onOrderStatusChanging (request) {
        return socketIO.updateStatus(request);
    });

    /**
     * Handles broadcast messages by forwarding them through Socket.IO
     *
     * @param {string} msg - Message type/event name
     * @param {any} data - Message data payload
     */
    bus.on(bus_events.broadcast, function (msg, data) {
        socketIO.emit(bus_events.broadcast, msg, data, options.env.Identity);
    });
};

export { initSocketIO };